import React, { useState, useEffect } from "react";
import { Plus, Trash2, Sparkles, Loader2, ChevronLeft, ChevronRight, Users, X, CalendarDays, Clock } from "lucide-react";
import { eventsApi, aiQuick } from "../api";
import SharePicker from "../SharePicker";

export default function CalendarApp() {
  const [events, setEvents] = useState([]);
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [selected, setSelected] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ title: "", date: "", time: "", notes: "", shared_with: [] });
  const [aiBusy, setAiBusy] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState("");

  const load = async () => setEvents(await eventsApi.list());
  useEffect(() => { load(); }, []);

  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const firstDay = new Date(month.getFullYear(), month.getMonth(), 1).getDay();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const dateStr = (day) => {
    const m = String(month.getMonth() + 1).padStart(2, "0");
    const d = String(day).padStart(2, "0");
    return `${month.getFullYear()}-${m}-${d}`;
  };

  const eventsForDay = (day) => events.filter(e => e.date === dateStr(day));

  const addEvent = async () => {
    if (!form.title || !form.date) return;
    try {
      await eventsApi.create(form);
      setForm({ title: "", date: "", time: "", notes: "", shared_with: [] });
      setShowAdd(false);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not create event.";
      alert(detail);
    }
  };

  const removeEvt = async (id) => {
    await eventsApi.remove(id);
    load();
  };

  const aiSuggest = async () => {
    setAiBusy(true);
    setAiSuggestion("");
    const list = events.slice(0, 30).map(e => `${e.date} ${e.time || ""} – ${e.title}`).join("\n") || "(none)";
    try {
      const r = await aiQuick(
        `Given today's date ${new Date().toISOString().slice(0, 10)} and these upcoming events:\n${list}\n\nSuggest 3 smart reminders or time-blocks I should add to my calendar this week. Be concise.`,
        "You are an AI calendar assistant."
      );
      setAiSuggestion(r.text);
    } catch { setAiSuggestion("AI unavailable."); }
    setAiBusy(false);
  };

  const monthLabel = month.toLocaleDateString([], { month: "long", year: "numeric" });

  return (
    <div className="h-full flex flex-col" data-testid="app-calendar">
      <div className="flex items-center justify-between p-3 border-b border-white/10">
        <div className="flex items-center gap-2">
          <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} className="btn-ghost p-1"><ChevronLeft size={14} /></button>
          <span className="text-sm font-medium w-32 text-center">{monthLabel}</span>
          <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} className="btn-ghost p-1"><ChevronRight size={14} /></button>
        </div>
        <div className="flex gap-2">
          <button data-testid="cal-ai-suggest" onClick={aiSuggest} className="btn-ghost text-xs flex items-center gap-1">
            {aiBusy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} color="#ff7a2e" />} AI Suggestions
          </button>
          <button data-testid="cal-add" onClick={() => setShowAdd(true)} className="btn-accent text-xs flex items-center gap-1">
            <Plus size={12} /> New Event
          </button>
        </div>
      </div>

      {aiSuggestion && (
        <div className="px-4 py-2 border-b border-white/10 bg-white/3 text-xs whitespace-pre-wrap text-white/80">{aiSuggestion}</div>
      )}

      <div className="flex-1 overflow-y-auto p-4">
        <div className="grid grid-cols-7 gap-2">
          {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d => (
            <div key={d} className="text-[10px] uppercase tracking-widest text-white/40 text-center">{d}</div>
          ))}
          {cells.map((day, i) => (
            <div
              key={i}
              data-testid={day ? `cal-day-${day}` : undefined}
              onClick={() => day && setSelected(dateStr(day))}
              className={`min-h-[72px] rounded-lg p-1.5 ${day ? "bg-white/3 border border-white/8 cursor-pointer hover:bg-white/6" : ""}`}
            >
              {day && (
                <>
                  <div className="text-[11px] text-white/60">{day}</div>
                  <div className="space-y-0.5 mt-1">
                    {eventsForDay(day).slice(0, 2).map(e => (
                      <div key={e.id} className="text-[10px] truncate px-1 rounded bg-[#ff7a2e]/20 text-[#ffb27a]">{e.title}</div>
                    ))}
                    {eventsForDay(day).length > 2 && (
                      <div className="text-[9px] text-white/40 px-1">+{eventsForDay(day).length - 2} more</div>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Day-detail pop-up — opens when the user clicks any date cell */}
      {selected && (
        <div
          data-testid="cal-day-popover"
          className="absolute inset-0 z-20 flex items-center justify-center p-4"
          style={{ background: "rgba(2,3,8,0.7)", backdropFilter: "blur(8px)" }}
          onClick={() => setSelected(null)}
        >
          <div
            className="glass rounded-xl w-full max-w-md max-h-[80%] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <div className="flex items-center gap-2">
                <CalendarDays size={14} className="text-[#a78bfa]" />
                <div>
                  <div className="text-sm font-medium text-white">
                    {new Date(selected + "T00:00:00").toLocaleDateString([], {
                      weekday: "long", month: "long", day: "numeric", year: "numeric"
                    })}
                  </div>
                  <div className="mono text-[10px] uppercase tracking-widest text-white/45">
                    {events.filter(e => e.date === selected).length} event{events.filter(e => e.date === selected).length === 1 ? "" : "s"}
                  </div>
                </div>
              </div>
              <button
                data-testid="cal-day-popover-close"
                onClick={() => setSelected(null)}
                className="btn-ghost p-1"
              >
                <X size={12} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {events.filter(e => e.date === selected).map((e) => (
                <div
                  key={e.id}
                  data-testid={`cal-event-${e.id}`}
                  className="bg-white/4 border border-white/10 rounded-lg p-3 space-y-1.5"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-white truncate">{e.title}</div>
                      {e.time && (
                        <div className="mono text-[10px] text-white/55 flex items-center gap-1 mt-0.5">
                          <Clock size={9} /> {e.time}
                        </div>
                      )}
                    </div>
                    <button
                      data-testid={`cal-event-delete-${e.id}`}
                      onClick={() => removeEvt(e.id)}
                      title="Delete event"
                      className="text-red-400 hover:bg-red-400/10 p-1 rounded"
                    >
                      <Trash2 size={11} />
                    </button>
                  </div>
                  {e.notes && (
                    <div className="text-xs text-white/70 whitespace-pre-wrap">{e.notes}</div>
                  )}
                  <div className="flex items-center flex-wrap gap-1.5 pt-0.5">
                    {e.shared_in && (
                      <span
                        data-testid={`event-shared-in-${e.id}`}
                        className="text-[9px] flex items-center gap-1 px-1.5 py-0.5 rounded bg-[#8b5cf6]/15 text-[#a78bfa] border border-[#8b5cf6]/30"
                        title={`Invited by ${e.owner}`}
                      >
                        <Users size={9} /> from {(e.owner || "").split("@")[0]}
                      </span>
                    )}
                    {!e.shared_in && (e.shared_with || []).length > 0 && (
                      <span
                        data-testid={`event-shared-out-${e.id}`}
                        className="text-[9px] flex items-center gap-1 px-1.5 py-0.5 rounded bg-white/8 text-white/70"
                        title={`Shared with: ${e.shared_with.join(", ")}`}
                      >
                        <Users size={9} /> shared · {e.shared_with.length}
                      </span>
                    )}
                  </div>
                </div>
              ))}
              {events.filter(e => e.date === selected).length === 0 && (
                <div className="text-center text-white/45 text-sm py-8">
                  <CalendarDays size={32} className="mx-auto mb-2 opacity-40" />
                  <div>No events on this day.</div>
                </div>
              )}
            </div>
            <div className="px-3 py-2 border-t border-white/10 flex justify-end gap-2">
              <button
                data-testid="cal-day-popover-add"
                onClick={() => { setForm(f => ({ ...f, date: selected })); setShowAdd(true); setSelected(null); }}
                className="btn-accent text-xs flex items-center gap-1"
              >
                <Plus size={11} /> Add event
              </button>
            </div>
          </div>
        </div>
      )}

      {showAdd && (
        <div className="absolute inset-0 bg-black/70 flex items-center justify-center z-10" onClick={() => setShowAdd(false)}>
          <div className="glass rounded-xl w-[420px] p-4 space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="text-sm font-medium">New Event</div>
            <input data-testid="cal-form-title" placeholder="Title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-sm" />
            <input data-testid="cal-form-date" type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-sm" />
            <input data-testid="cal-form-time" type="time" value={form.time} onChange={e => setForm({ ...form, time: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-sm" />
            <textarea placeholder="Notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-sm" rows={2} />
            <SharePicker
              value={form.shared_with}
              onChange={(arr) => setForm(f => ({ ...f, shared_with: arr }))}
              label="Invite teammates"
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowAdd(false)} className="btn-ghost text-xs">Cancel</button>
              <button data-testid="cal-form-save" onClick={addEvent} className="btn-accent text-xs">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
